import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Reusable confirmation dialog for destructive / irreversible actions.
 * Controlled: pass `open`, `onOpenChange`, and `onConfirm`.
 *
 * Example:
 *   const [confirm, setConfirm] = useState<null | (() => void)>(null);
 *   ...
 *   <ConfirmDialog
 *     open={!!confirm}
 *     onOpenChange={(o) => !o && setConfirm(null)}
 *     title="Suspend this user?"
 *     description="They will lose access until reactivated."
 *     confirmText="Suspend"
 *     destructive
 *     onConfirm={() => { confirm?.(); setConfirm(null); }}
 *   />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title = "Are you sure?",
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  destructive = false,
  loading = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  loading?: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={loading}
            className={destructive ? "bg-destructive text-white hover:bg-destructive/90" : undefined}
          >
            {loading ? "Working…" : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
